import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import {useTheme} from '@mui/material/styles';
import List from '@mui/material/List';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import { Typography } from '@mui/material';

// components
import Iconify from '../../components/iconify';
import { useSettingsContext } from '../../components/settings';
import { getFinancialData } from '../../components/upload';
import { fEuroNumber } from '../../utils/format-number';

const AlertasFinancieras = [
    {tag: "tiene_nomina", label: "Tiene Nómina", icon: "ic:baseline-attach-money" },
    {tag: "perdida_nomina", label: "Pérdida de Nómina", icon: "ic:baseline-money-off" },
    {tag: "tiene_subsidio", label: "Tiene Subsidio", icon: "solar:hand-money-bold" },
    {tag: "tiene_recobro", label: "Tiene Recobro", icon: "solar:money-bag-bold" },
    {tag: "tiene_embargo", label: "Tiene Embargo", icon: "solar:forbidden-bold" },
    {tag: "tiene_recibo_telecom_devuelto_mes", label: "Recibo Telecom Devuelto Mes", icon: "solar:document-bold" },
    {tag: "tiene_recibo_utilities_devuelto_mes", label: "Recibo Utilities Devuelto Mes", icon: "solar:document-bold" },
    {tag: "tiene_prestamo_constituido_mes", label: "Préstamo Constituido Mes", icon: "mdi:bank" },
    {tag: "tiene_descubierto", label: "Tiene Descubierto", icon: "solar:danger-triangle-bold" },
    {tag: "tiene_tarjetacredito", label: "Tiene Tarjeta Crédito", icon: "solar:card-bold" },
    {tag: "tiene_cuentaahorro", label: "Tiene Cuenta Ahorro", icon: "solar:wallet-bold" },
    {tag: "tiene_plandepensiones", label: "Tiene Plan de Pensiones", icon: "solar:document-bold" },
    {tag: "tiene_ahorros_fuera", label: "Tiene Ahorros Fuera", icon: "solar:wallet-bold" },
]

const PerfilSociodemografico = [
    { tag: "es_empleado_reciente", label: "Empleado Reciente", icon: "solar:user-bold" },
    { tag: "es_funcionario", label: "Es Funcionario", icon: "solar:user-bold" },
    { tag: "tiene_mascota", label: "Tiene Mascota", icon: "cil:dog" },
    { tag: "tiene_hijos", label: "Tiene Hijos", icon: "solar:users-group-rounded-bold" },
    { tag: "es_fumador", label: "Es Fumador", icon: "cil:smoke" },
    { tag: "tiene_seguro_coche", label: "Tiene Seguro Coche", icon: "tabler:car-filled" },
    { tag: "tiene_seguro_hogar", label: "Tiene Seguro Hogar", icon: "solar:home-bold" },
    { tag: "tiene_seguro_vida", label: "Tiene Seguro Vida", icon: "solar:heart-bold" },
    { tag: "regimen_vivienda", label: "Régimen Vivienda", icon: "solar:home-bold" },
    { tag: "regimen_contrato", label: "Régimen Contrato", icon: "solar:document-bold" },
]

export default function FinancialAlerts() {
    const navigate = useNavigate();
    const settings = useSettingsContext();
    const theme = useTheme();
    const [resumen, setResumen] = useState(null);
    
    useEffect(() => {
        try {
            const data = getFinancialData();
            if (!data) throw new Error('No data');
            setResumen(data);
            settings.onFiles();
        } catch (error) {
            console.warn('No se encontraron datos. Redirigiendo al inicio...');
            navigate('/');
        }
    }, [navigate, settings]);
    
    if (!resumen) return null;
    
    return (
        <>
            <Card sx={{ flex: 1, mb: 3 }}>
            <CardContent>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="stretch">
                    <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <CardHeader title="Número de Financiaciones" />
                        <CardContent sx={{ flexGrow: 1 }}>
                            <Typography variant="h3">{fEuroNumber(resumen.alertas.num_financiaciones || '0')}</Typography>
                        </CardContent>
                    </Card>
                    <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <CardHeader title="Número BNPL" />
                        <CardContent sx={{ flexGrow: 1 }}>
                            <Typography variant="h3">{fEuroNumber(resumen.alertas.num_bnlp || '0')}</Typography>
                        </CardContent>
                    </Card>
                    <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <CardHeader title="Número de Recibos Domiciliados" />
                        <CardContent sx={{ flexGrow: 1 }}>
                            <Typography variant="h3">{fEuroNumber(resumen.alertas.num_recibos_domiciliados || '0')}</Typography>
                        </CardContent>
                    </Card>
                </Stack>
            </CardContent>
            </Card>

            <Card>
            <CardContent>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="stretch">
                <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Stack backgroundColor={theme.palette.info.lighter} sx={{ p: 2, borderRadius: 2, flexGrow: 1 }}>
                    <CardHeader title="Alertas Financieras" />
                    <CardContent sx={{ flexGrow: 1 }}>
                        <List>
                        {AlertasFinancieras.filter((option) => resumen.alertas[option.tag]).map((alerta, idx) => (
                            <ListItemButton key={idx}>
                            <ListItemIcon>
                                <Iconify icon={alerta.icon} width={24} />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    resumen.alertas[alerta.tag] !== undefined ? (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Typography component="span">{alerta.label}</Typography>
                                        <Iconify icon="icon-park-solid:correct" color={theme.palette.tertiary.main}  width={24} />
                                        </span>
                                    ) : (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Typography component="span">{alerta.label}</Typography>
                                        <Typography component="span">{String(resumen.alertas[alerta.tag])}</Typography>
                                        </span>
                                    )
                                }
                            />
                            </ListItemButton>
                        ))}
                        </List>
                        <List>
                            {AlertasFinancieras.filter((option) => !resumen.alertas[option.tag]).map((alerta, idx) => (
                            <ListItemButton key={idx}>
                            <ListItemIcon>
                                <Iconify icon={alerta.icon} width={24} />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    resumen.alertas[alerta.tag] !== undefined ? (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Typography component="span">{alerta.label}</Typography>
                                        <Iconify icon="icon-park-solid:error" color={theme.palette.primary.dark} width={24} />
                                        </span>
                                    ) : (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Typography component="span">{alerta.label}</Typography>
                                        <Typography component="span">{String(resumen.alertas[alerta.tag])}</Typography>
                                        </span>
                                    )
                                }
                            />
                            </ListItemButton>
                        ))}
                        </List>
                    </CardContent>
                    </Stack>
                </Card>

                <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Stack backgroundColor={theme.palette.warning.light} sx={{ p: 2, borderRadius: 2, flexGrow: 1 }}>
                    <CardHeader title="Perfil Sociodemográfico" />
                    <CardContent sx={{ flexGrow: 1 }}>
                        <List>
                        {PerfilSociodemografico.filter((option) => resumen.alertas[option.tag]).map((alerta, idx) => (
                            <ListItemButton key={idx}>
                            <ListItemIcon>
                                <Iconify icon={alerta.icon} width={24} />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                resumen.alertas[alerta.tag] !== undefined && resumen.alertas[alerta.tag] === true ? (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Typography component="span">{alerta.label}</Typography>
                                    <Iconify icon="icon-park-solid:correct" color={theme.palette.tertiary.main} width={24} />
                                    </span>
                                ) : (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Typography component="span">{alerta.label}</Typography>
                                    <Typography component="span">{String(resumen.alertas[alerta.tag])}</Typography>
                                    </span>
                                )
                                }
                            />
                            </ListItemButton>
                        ))}
                        </List>
                        <List>
                        {PerfilSociodemografico.filter((option) => !resumen.alertas[option.tag]).map((alerta, idx) => (
                            <ListItemButton key={idx}>
                            <ListItemIcon>
                                <Iconify icon={alerta.icon} width={24} />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                resumen.alertas[alerta.tag] !== undefined ? (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Typography component="span">{alerta.label}</Typography>
                                    <Iconify icon="icon-park-solid:error" color={theme.palette.primary.dark} width={24} />
                                    </span>
                                ) : (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Typography component="span">{alerta.label}</Typography>
                                    <Typography component="span">{String(resumen.alertas[alerta.tag])}</Typography>
                                    </span>
                                )
                                }
                            />
                            </ListItemButton>
                        ))}
                        </List>
                    </CardContent>
                    </Stack>
                </Card>
                </Stack>
            </CardContent>
            </Card>
        </>
    )
}